import { sentryEnabled, Sentry } from "../sentry.ts";

/**
 * Unified API error.
 *
 * Mirrors the backend's standard error envelope ``{ detail, code? }``. The
 * `message` is the user-facing string (toast text); machine-readable details
 * stay on the instance so callers can branch on them.
 *
 * Throwing an ApiError automatically records a Sentry breadcrumb describing
 * the failed request. If the error bubbles up and Sentry captures it,
 * the breadcrumb is attached, so the event in Sentry has request context
 * without each call site needing to wire it up.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  readonly detail: string;
  readonly path: string;
  readonly method: string;

  constructor(args: {
    status: number;
    code?: string;
    detail: string;
    path: string;
    method: string;
  }) {
    super(args.detail);
    this.name = "ApiError";
    this.status = args.status;
    this.code = args.code;
    this.detail = args.detail;
    this.path = args.path;
    this.method = args.method;
  }
}

/** Pull `{detail, code}` out of a fetch Response. Falls back to statusText
 *  if the body isn't JSON. Never throws — safe to call before throwing. */
async function parseErrorBody(
  res: Response,
): Promise<{ detail: string; code?: string }> {
  try {
    const body = await res.json();
    return {
      detail: typeof body?.detail === "string" ? body.detail : res.statusText,
      code: typeof body?.code === "string" ? body.code : undefined,
    };
  } catch {
    return { detail: res.statusText };
  }
}

/** Build + record an ApiError. Records a Sentry breadcrumb so subsequent
 *  captureException calls have the failing request attached. */
export async function apiErrorFromResponse(
  res: Response,
  path: string,
  method: string,
): Promise<ApiError> {
  const { detail, code } = await parseErrorBody(res);
  const err = new ApiError({ status: res.status, code, detail, path, method });
  if (sentryEnabled) {
    Sentry.addBreadcrumb({
      category: "api",
      level: res.status >= 500 ? "error" : "warning",
      message: `${method} ${path} → ${res.status}`,
      data: { status: res.status, code, detail },
    });
  }
  return err;
}

/** Record a network-level failure (fetch rejected before a Response). */
export function reportNetworkError(
  err: unknown,
  path: string,
  method: string,
): Error {
  const wrapped = err instanceof Error ? err : new Error(String(err));
  if (sentryEnabled) {
    Sentry.addBreadcrumb({
      category: "api",
      level: "error",
      message: `${method} ${path} → network error`,
      data: { message: wrapped.message },
    });
  }
  return wrapped;
}
